import axios from 'axios';
import dayjs from 'dayjs';
import { useCallback } from 'react';

import { CommonRes } from '@/types';

import { useAppFeedback } from './useAppFeedback';

interface ApiHandlerOptions {
  successMessage?: string;
  errorMessage?: string;
  showLoading?: boolean;
  showDetailedErrors?: boolean;
  context?: string;
}

interface ExtendedErrorResponse extends CommonRes<null> {
  type?: string;
  details?: unknown;
  stack?: string;
  errorId?: string;
  timestamp?: string;
  path?: string;
  method?: string;
  requestId?: string;
}

export const useApiHandler = () => {
  const { message, notification } = useAppFeedback();

  const handleApiCall = useCallback(async <T>(
    apiCall: () => Promise<CommonRes<T>>,
    options: ApiHandlerOptions = {},
  ): Promise<T | null> => {
    const {
      successMessage,
      errorMessage,
      showLoading = false,
      showDetailedErrors = process.env['NODE_ENV'] === 'development',
    } = options;

    let loadingMessage: ReturnType<typeof message.loading> | null = null;
    if (showLoading) {
      loadingMessage = message.loading('處理中...', 0);
    }

    try {
      const result = await apiCall();

      if (result.code === 0) {
        if (loadingMessage) {
          loadingMessage();
        }
        if (successMessage) {
          message.success(successMessage);
        }
        return result.data;
      } else {
        const extendedResult = result as ExtendedErrorResponse;

        const displayMessage =
          result.message || errorMessage || '操作失敗，請稍後再試';

        console.group('🚨 API 錯誤詳情');
        console.error('錯誤代碼:', result.code);
        console.error('錯誤消息:', result.message);
        console.error('錯誤類型:', extendedResult.type);
        console.error('請求路徑:', extendedResult.path);
        console.error('請求方法:', extendedResult.method);
        console.error('錯誤 ID:', extendedResult.errorId);
        console.error('請求 ID:', extendedResult.requestId);
        console.error('時間戳:', extendedResult.timestamp);
        if (extendedResult.details) {
          console.error('詳細信息:', extendedResult.details);
        }
        if (extendedResult.stack) {
          console.error('堆棧追蹤:', extendedResult.stack);
        }
        console.groupEnd();

        if (loadingMessage) {
          loadingMessage();
        }
        message.error(displayMessage || '操作失敗，請稍後再試');

        if (showDetailedErrors && extendedResult.errorId) {
          setTimeout(() => {
            notification.info({
              message: '錯誤追蹤',
              description: `錯誤 ID: ${extendedResult.errorId}`,
              duration: 5,
            });
          }, 1000);
        }

        return null;
      }
    } catch (error) {
      console.group('🔥 網路錯誤詳情');
      console.error('原始錯誤:', error);

      let displayMessage = errorMessage;
      let errorDetails: Record<string, unknown> = {};

      if (axios.isAxiosError(error)) {
        const response = error.response;
        const request = error.request;

        errorDetails = {
          status: response?.status,
          statusText: response?.statusText,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          timeout: error.config?.timeout,
        };

        console.error('HTTP 狀態:', response?.status);
        console.error('請求 URL:', error.config?.url);
        console.error('請求方法:', error.config?.method?.toUpperCase());

        if (response?.data) {
          const errorData = response.data as ExtendedErrorResponse;
          console.error('響應數據:', errorData);

          if (errorData.message) {
            displayMessage = errorData.message;
          }

          if (errorData['errorId']) {
            console.error('服務器錯誤 ID:', errorData['errorId']);
            errorDetails['errorId'] = errorData['errorId'];
          }
          if (errorData['requestId']) {
            console.error('請求 ID:', errorData['requestId']);
            errorDetails['requestId'] = errorData['requestId'];
          }
          if (errorData.details) {
            console.error('詳細信息:', errorData.details);
          }

          if (loadingMessage) {
            loadingMessage();
          }
          message.error(displayMessage || '操作失敗，請稍後再試');

          if (showDetailedErrors && errorData['errorId']) {
            setTimeout(() => {
              notification.info({
                message: '錯誤追蹤',
                description: `錯誤 ID: ${errorData['errorId']}`,
                duration: 5,
              });
            }, 1000);
          }
        } else if (request) {
          console.error('網路請求失敗 - 無響應');
          displayMessage = '網路連接失敗，請檢查網路連接';
          if (loadingMessage) {
            loadingMessage();
          }
          message.error(displayMessage || '操作失敗，請稍後再試');
        } else {
          console.error('請求配置錯誤:', error.message);
          displayMessage = '請求配置錯誤';
          if (loadingMessage) {
            loadingMessage();
          }
          message.error(displayMessage || '操作失敗，請稍後再試');
        }

        if (response?.status && !response.data?.message) {
          const specificMessage = getStatusSpecificMessage(response.status);
          if (specificMessage !== displayMessage) {
            setTimeout(() => {
              message.warning(specificMessage, 4);
            }, 500);
          }
        }
      } else {
        console.error('非網路錯誤:', error);
        displayMessage = errorMessage || '發生未知錯誤，請稍後再試';
        if (loadingMessage) {
          loadingMessage();
        }
        message.error(displayMessage || '操作失敗，請稍後再試');
      }

      console.groupEnd();

      if (process.env['NODE_ENV'] === 'development') {
        // Log error to console for development debugging
        console.error('API Error:', {
          timestamp: dayjs().toISOString(),
          error: error instanceof Error ? error.message : String(error),
          details: errorDetails,
          stack: error instanceof Error ? error.stack : undefined,
        });
      }

      return null;
    }
  }, [message, notification]);

  return {
    handleApiCall,
  };
};

function getStatusSpecificMessage(status: number): string {
  switch (status) {
    case 400:
      return '請求參數錯誤，請檢查輸入的資料';
    case 401:
      return '登入已過期，請重新登入';
    case 403:
      return '權限不足，無法執行此操作';
    case 404:
      return '請求的資源不存在';
    case 409:
      return '資料衝突，該資源可能已存在';
    case 422:
      return '資料驗證失敗，請檢查必填欄位';
    case 429:
      return '請求過於頻繁，請稍後再試';
    case 500:
      return '伺服器發生錯誤，請稍後再試';
    case 502:
      return '網關錯誤，請稍後再試';
    case 503:
      return '服務暫時無法使用，請稍後再試';
    case 504:
      return '請求超時，請稍後再試';
    default:
      return `發生未知錯誤 (${status})`;
  }
}
